import { barcodeAamvaDataSchema } from '../../../../backend/features/barcodesAamva/shared';
import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { defineColumns } from '@juvare/platform-shared/features/storage';
import { z } from 'zod';
import { badgeEntityName } from './BadgeEntityTypes';
export const accountBadgeEntityName = 'accountBadge';
export const accountBadgeEntityIdSequence = 'accountBadgeEntityIdSequence';
export const accountBadgeStatusReasonSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    canReissue: z.boolean().optional(),
});
export const accountBadgeStatuses = ['valid', 'expired', 'revoked', 'reissueNeeded'];
export const accountBadgeDistributionStatuses = ['not-printed', 'processing', 'printed', 'distributed'];
export const accountBadgeStatusSchema = z.enum(accountBadgeStatuses);
export const accountBadgeDistributionStatusSchema = z.enum(accountBadgeDistributionStatuses);
export const barcodeAamvaDataSnapshotSchema = barcodeAamvaDataSchema.extend({
    ansiHeader: z.string(),
    aamvaDataSettings: z.object({}).passthrough().optional(),
});
export const accountBadgeSchema = z.object({
    /**
     * Required to be set before creation:
     * `onTransform()` is async when executing inside `context.utils.executeInTransaction()`
     */
    accountBadgeNumericId: z.number(),
    badgeId: z.string().uuid(),
    accountId: z.string().uuid(),
    status: accountBadgeStatusSchema,
    statusReason: accountBadgeStatusReasonSchema.optional(),
    /**
     * this Account Badge was "replaced" (reissue) by other one
     */
    reissuedBy: z
        .object({
        accountBadgeId: z.string().uuid(),
        reissuedAt: z.number(),
    })
        .optional(),
    /**
     * this Account Badge is "replacing" (reissue) other one
     */
    reissueHistory: z
        .object({
        accountBadgeId: z.string().uuid(),
        reissuedAt: z.number(),
        fromStatus: accountBadgeStatusSchema,
    })
        .array()
        .optional(),
    issuedAt: z.number(),
    expiresAt: z.number(),
    statusLastUpdatedAt: z.number().optional(),
    distributionStatusLastUpdatedAt: z.number().optional(),
    distributionStatus: accountBadgeDistributionStatusSchema,
    // hint: can extend entity to support additional options fields
    options: z
        .object({
        organizationId: z.string().uuid().optional(),
    })
        .passthrough(),
    barcodeDataSnapshot: barcodeAamvaDataSnapshotSchema.optional(),
});
export const accountBadgeColumns = defineColumns({
    accountBadgeNumericId: {
        type: 'number-required',
        index: true,
    },
    badgeId: {
        type: 'foreign-key',
        entity: badgeEntityName,
        onDeleteCascade: false,
    },
    accountId: {
        type: 'foreign-key',
        entity: accountEntityName,
        onDeleteCascade: false,
    },
    organizationId: {
        type: 'string',
    },
    issuedAt: {
        type: 'number',
    },
    status: {
        type: 'string-required',
    },
    distributionStatus: {
        type: 'string-required',
    },
    expiresAt: {
        type: 'number-required',
    },
    statusLastUpdatedAt: {
        type: 'number',
    },
    distributionStatusLastUpdatedAt: {
        type: 'number',
    },
    statusReasonTitle: {
        type: 'string',
    },
    statusReasonDescription: {
        type: 'string',
    },
    statusReasonCanReissue: {
        type: 'boolean',
    },
    reissuedByAccountBadgeId: {
        type: 'string',
    },
    accountDisplayName: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'displayName',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    accountSystemRole: {
        type: 'foreign-column',
        foreignEntity: accountEntityName,
        foreignColumn: 'systemRole',
        foreignColumnType: 'string',
        mappedByColumn: 'accountId',
    },
    previousAccountBadgeIds: {
        type: 'string-array',
        index: true,
    },
});
